import { useState, useEffect } from "react";
import logoWhiteSvg from "../../../branding/logo_white.svg";
import { getPageStatuses, getFeatureFlags } from "../utils/adminStore";

interface SocialLink {
  name: string;
  url: string;
  label: string;
  icon: React.ReactNode;
}

const socialLinks: SocialLink[] = [
  {
    name: "LinkedIn",
    url: "https://www.linkedin.com/company/radiece",
    label: "Follow RADIECE on LinkedIn (Opens in new tab)",
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
      </svg>
    ),
  },
  {
    name: "YouTube",
    url: "https://www.youtube.com/@radiece",
    label: "Subscribe to RADIECE on YouTube (Opens in new tab)",
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    name: "X (Twitter)",
    url: "https://x.com/radiece",
    label: "Follow RADIECE on X (Opens in new tab)",
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    name: "Instagram",
    url: "https://www.instagram.com/radiece.official",
    label: "Follow RADIECE on Instagram (Opens in new tab)",
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path fillRule="evenodd" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" clipRule="evenodd" />
      </svg>
    ),
  },
];

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [pageStatuses, setPageStatuses] = useState(getPageStatuses());
  const [flags, setFlags] = useState(getFeatureFlags());

  useEffect(() => {
    const sync = () => {
      setPageStatuses(getPageStatuses());
      setFlags(getFeatureFlags());
    };
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  const isPublished = (path: string) => {
    if (path === "/careers" && !flags.enableCareers) return false;
    if (path === "/pricing" && !flags.enablePricingCalculator) return false;
    const status = pageStatuses.find((p) => p.path === path);
    return !status || status.status === "published";
  };

  return (
    <footer
      className="no-print bg-[#080d16] text-slate-300 border-t border-slate-800/80 transition-colors"
      aria-label="Site Footer"
    >
      <h2 className="sr-only">Footer Navigation & Company Directory</h2>

      {/* Top Value / Fast Connect Banner */}
      <div className="border-b border-slate-800/60 bg-[#0a111c]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
            {/* Direct Helpline */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-2.826-1.47-5.114-3.758-6.584-6.584l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Direct Helpline</span>
                <a
                  href="tel:+919567551437"
                  className="font-semibold text-white hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                >
                  +91 95675 51437
                </a>
              </div>
            </div>

            {/* Official Desk */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Official Inquiries</span>
                <a
                  href="mailto:connect@radiece.com"
                  className="font-semibold text-white hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                >
                  connect@radiece.com
                </a>
              </div>
            </div>

            {/* Business Operations */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Working Schedule</span>
                <span className="font-semibold text-slate-200">Mon – Sat: 09:00 – 18:00 IST</span>
              </div>
            </div>

            {/* Turnaround Commitment */}
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.05]">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/20">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Turnaround SLA</span>
                <span className="font-semibold text-slate-200">24-Hour Response Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Directory & Navigation Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Corporate Profile Column (5 columns on large screens) */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center gap-3">
              <img
                src={logoWhiteSvg}
                alt="RADIECE Logo"
                width={150}
                height={32}
                className="h-8 w-auto object-contain"
              />
              <span className="bg-primary/10 text-primary border border-primary/20 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                Pvt Ltd
              </span>
            </div>

            <p className="text-xs font-semibold text-primary tracking-wide">
              Intelligence, Engineered.
            </p>

            <p className="text-xs text-slate-400 leading-relaxed max-w-md">
              <strong className="text-slate-200 font-semibold">RADIECE</strong> is an acronym for{" "}
              <span className="text-slate-300">
                Research And Development Infrastructure for Electronic and Computing Equipment
              </span>
              . An engineering enterprise specializing in software platforms, embedded IoT, robotics, and advanced technical upskilling.
            </p>

            {/* Corporate Identification Badges */}
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300">
                <span className="text-primary font-bold">CIN:</span>
                <span>U58200KL2026PTC104862</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300">
                <span className="text-primary font-bold">GSTIN:</span>
                <span>32AAQCR1162B1ZR</span>
              </div>
            </div>

            {/* Social Channels */}
            <div className="pt-2">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                Connect With Engineering
              </p>
              <div className="flex items-center gap-2">
                {socialLinks.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-primary text-slate-300 hover:text-slate-950 border border-white/[0.08] hover:border-primary flex items-center justify-center transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Solutions Column (3 columns on large screens) */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-white border-b border-white/[0.08] pb-2">
              Engineering Solutions
            </h3>
            <nav aria-label="Engineering Solutions Navigation">
              <ul className="space-y-2 text-xs">
                <li>
                  <a
                    href="/services"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    IT & ITeS Software Development
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    SaaS & Digital Platforms (RaBuOS)
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Applied R&D & Hardware Prototyping
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Robotics & Industrial Automation
                  </a>
                </li>
                <li>
                  <a
                    href="/services"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Security & IP Surveillance Systems
                  </a>
                </li>
                {isPublished("/pricing") && (
                  <li>
                    <a
                      href="/pricing"
                      className="text-primary font-medium hover:text-white transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                    >
                      Transparent Commercial Pricing →
                    </a>
                  </li>
                )}
              </ul>
            </nav>
          </div>

          {/* Technical Education Column (2 columns on large screens) */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-white border-b border-white/[0.08] pb-2">
              Education & Skills
            </h3>
            <nav aria-label="Technical Education Navigation">
              <ul className="space-y-2 text-xs">
                <li>
                  <a
                    href="/courses"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    All Technology Courses
                  </a>
                </li>
                <li>
                  <a
                    href="/courses"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Python Full-Stack
                  </a>
                </li>
                <li>
                  <a
                    href="/courses"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    MERN / MEAN Stack
                  </a>
                </li>
                <li>
                  <a
                    href="/courses"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Embedded Systems & IoT
                  </a>
                </li>
                <li>
                  <a
                    href="/courses"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Software Testing & QA
                  </a>
                </li>
                <li>
                  <a
                    href="/courses"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Corporate Tech Training
                  </a>
                </li>
              </ul>
            </nav>
          </div>

          {/* Corporate & Legal Column (2 columns on large screens) */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-white border-b border-white/[0.08] pb-2">
              Corporate & Trust
            </h3>
            <nav aria-label="Corporate & Legal Navigation">
              <ul className="space-y-2 text-xs">
                <li>
                  <a
                    href="/about"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    About RADIECE
                  </a>
                </li>
                {isPublished("/careers") && (
                  <li>
                    <a
                      href="/careers"
                      className="text-slate-400 hover:text-primary transition-colors inline-flex items-center gap-1.5 py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                    >
                      <span>Careers</span>
                      <span className="bg-emerald-500/20 text-emerald-300 text-[10px] font-bold px-1.5 py-0.2 rounded">
                        Hiring
                      </span>
                    </a>
                  </li>
                )}
                {isPublished("/contact") && (
                  <li>
                    <a
                      href="/contact"
                      className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                    >
                      Technical Inquiry Desk
                    </a>
                  </li>
                )}
                <li>
                  <a
                    href="/blog"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Technical Blog
                  </a>
                </li>
                <li>
                  <a
                    href="/news"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    News & Dispatches
                  </a>
                </li>
                <li>
                  <a
                    href="/security"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Security & Bug Bounty
                  </a>
                </li>
                <li>
                  <a
                    href="/amc"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    AMC & Support Terms
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Terms & Conditions
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/data-storage"
                    className="text-slate-400 hover:text-primary transition-colors inline-block py-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded"
                  >
                    Data Storage Policy
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      {/* Statutory Address & Bottom Compliance Strip */}
      <div className="border-t border-slate-800/80 bg-[#060a12] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-6 text-center lg:text-left">
            {/* Registered Office Details */}
            <div className="space-y-1 max-w-2xl">
              <div className="flex items-center justify-center lg:justify-start gap-1.5 text-xs text-slate-300 font-medium">
                <svg className="w-3.5 h-3.5 text-primary shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                <span>Registered Office Address:</span>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Building No. 24/1701, Suite No. 1034, Heiley Offices, 2nd Floor KC Arcade, Near TV Centre, CSEZ P.O, Kakkanad, Ernakulam - 682037, Kerala, India
              </p>
              <p className="text-[11px] text-slate-500">
                For statutory correspondence purposes only. Physical office visits by prior technical appointment.
              </p>
            </div>

            {/* Copyright & System Status */}
            <div className="flex flex-col items-center lg:items-end gap-2 shrink-0">
              <div className="flex items-center gap-2 text-xs font-mono text-slate-400">
                <span className="relative flex h-2 w-2">
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
                </span>
                <span>System Status: Fully Operational</span>
              </div>
              <p className="text-xs text-slate-400">
                &copy; {currentYear} RADIECE PRIVATE LIMITED. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
